
import { defineComponent } from "vue";
import AppFooter from "@/components/common/AppFooter.vue";
import { mapGetters, mapMutations } from "vuex";
import { iBenefitSummary, iCalculateRate } from "@/types/iBenefitsData";
import common from "@/config/common";
import helper from "@/utils/helper";
import benefitMapper from "@/utils/benefitMapper";
import messages from "@/config/messages";
import eventBus from "@/utils/eventBus";
import * as apiClient from "@/apiclient/";
import ApprovalModal from "../ApprovalModal.vue";
import ToolTip from "@/components/common/ToolTip.vue";
import iBenefit from "@/types/iBenefit";
import iDependent from "@/types/iDependent";
import iOpenEnrollmentBenefitTier from "@/types/iOpenEnrollmentBenefitTier";

export default defineComponent({
  components: {
    AppFooter,
    ApprovalModal,
    ToolTip,
  },
  data() {
    return {
      accordionTitle: "Accordion Title",
      benefits: [] as iBenefit[],
      openEnrollments: [] as apiClient.OpenEnrollmentBenefitsDto[],
      bundles: [] as apiClient.OpenEnrollmentBenefitsBundleDto[],
      unbundledBenefits: [] as apiClient.OpenEnrollmentBenefitDto[],
      dependents: [] as iDependent[],
      paymentMethods: {} as any,
      profile: {} as apiClient.ProfileResponseDto,
      common,
      validationMsg: "",
      helper,
      action: { enroll: "enroll", waive: "waive" },
      calculateRates: [] as iCalculateRate[],
      lastCalculatedRates: [] as iCalculateRate[],
      benefitEffectiveDate: "",
      hasFileDeleteCheckHappened: false,
      isApprovalModalVisible: false,
      approvalModalText: "",
      customerRules: [] as apiClient.OpenEnrollmentCustomerRules[],
    };
  },
  async created() {
    eventBus.emit("toggle-enrollments-menu", false);
    //await this.getPaymentMethods();
    await this.getAllDependents();
    await this.getAllBenefits();

    await this.getProfileInfo();
    await this.calculateBenefitRates();
    window.addEventListener("beforeunload", this.confirmBeforeUnload);
    this.benefitEffectiveDate =
      this.getEnrollment.openEnrollment.benefitEffectiveDate;
  },
  beforeUnmount() {
    // Remove the beforeunload event handler when the component is destroyed
    window.removeEventListener("beforeunload", this.confirmBeforeUnload);
  },
  computed: {
    ...mapGetters(["getBenefitAmount", "getEnrollment"]),
    benefitsValidationMessage() {
      return this.getBenefitsValidationMessage();
    },
    isEnrollmentRestricted(): boolean {
      return this.getEnrollment.openEnrollment.customerRules === 1;
    },
  },
  watch: {
    calculateRates: {
      async handler(newVal, oldVal) {
        const lastVal = JSON.stringify(this.lastCalculatedRates);
        const newValStr = JSON.stringify(newVal);

        if (lastVal !== newValStr) {
          await this.calculateBenefitRates();
          this.lastCalculatedRates = JSON.parse(newValStr);
        }
      },
      deep: true,
    },
  },
  methods: {
    ...mapMutations(["setBenefitAmount", "setFormDirty", "setBenefitRates"]),
    confirmBeforeUnload(event: any) {
      helper.confirmBeforeUnload(event);
    },
    canModifyBenefit(benefit: iBenefit): boolean {
      // If no global restriction, allow modifications
      if (!this.isEnrollmentRestricted) return true;
      // If restricted, allow changes only for benefits the participant is currently enrolled in
      return benefit?.currentlyEnrolled;
    },
    async downloadDocument(custBenefitDocumentID: number) {
      await this.getDocument(custBenefitDocumentID);
    },
    async getDocument(custBenefitDocumentID: number): Promise<void> {
      const result = await apiClient
        .documentController()
        .getApiDocumentGetBenefitDocument({
          CustBenefitDocumentID: custBenefitDocumentID,
        });
      if (!result || result.error) {
        if (result.error) {
          this.validationMsg = result.error + messages.errorMsg;
        }
        return;
      }

      if (!result.data) {
        return;
      }

      let fileData = result.data.fileData!;
      try {
        const blob = new Blob([helper.base64ToUint8Array(fileData)], {
          type: result.data.contentType! || "application/octet-stream",
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", result.data.fileName! ?? "");
        document.body.appendChild(link);
        link.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(link);
      } catch (e) {
        this.validationMsg = "Failed to decode or download file.";
        console.error("File download error:", e, fileData.slice(0, 100));
      }
    },
    toggleDocuments(index: number) {
      this.benefits[index].documentsOpen = !this.benefits[index].documentsOpen;
    },
    toggleBundleDocuments(benefit: any, index: number) {
      benefit.benefits[index].documentsOpen =
        !benefit.benefits[index].documentsOpen;
    },
    toggleAttribute(isOpen: boolean) {
      return isOpen ? "collapse" : "";
    },
    async getPaymentMethods() {
      const result = await apiClient.paymentController().getApiPaymentType();
      if (!result || result.error) {
        if (result.error) {
          this.validationMsg = result.error + messages.errorMsg;
        }
        return;
      } else if (result.data?.value) {
        this.paymentMethods = result.data.value;
      }
    },
    async tierSelected(
      isSelected: boolean,
      tierPrice: number,
      selectedBenefit: iBenefit,
      index: number,
      optionIndex: number
    ) {
      // Remove any existing entry for this benefit
      const indexToRemove = this.calculateRates.findIndex(
        (item) => item.benefitId === selectedBenefit.benefitId
      );
      if (indexToRemove !== -1) {
        this.calculateRates.splice(indexToRemove, 1);
        this.calculateRates = [...this.calculateRates];
      }

      if (selectedBenefit.benefitType === 1) {
        this.bundledTierSelected(
          isSelected,
          tierPrice,
          selectedBenefit,
          index,
          optionIndex
        );
        return;
      }

      this.benefits[index].dependentValMsg = "";
      this.setFormDirty(true);
      if (
        this.benefits[index] &&
        Array.isArray(this.benefits[index].dependents)
      ) {
        this.benefits[index].dependents!.map((dependent: iDependent) => {
          dependent.isSelected = false;
          return dependent;
        });
      }
      if (isSelected) {
        this.enrollBenefit(index);

        const existingBenefit = this.calculateRates.find(
          (item) => item.benefitId === selectedBenefit.benefitId
        );
        if (existingBenefit) {
          existingBenefit.tierLevelName =
            this.benefits[index].tiers[optionIndex].tierLevelName!;
        } else {
          this.calculateRates.push({
            benefitId: this.benefits[index].benefitId,
            tierLevelName:
              this.benefits[index].tiers[optionIndex].tierLevelName!,
          });
          this.calculateRates = [...this.calculateRates];
        }
        this.benefits[index].tiers.map((option, index) => {
          if (index !== optionIndex) {
            option.isSelected = false;
          }
          return option;
        });

        this.disableDependents(index, optionIndex);
        var allDisabled = false;
        if (
          this.benefits[index] &&
          Array.isArray(this.benefits[index].dependents!)
        ) {
          allDisabled = this.benefits[index].dependents!.every(
            (dependent: iDependent) => {
              dependent.disabled;
            }
          );
        }
        if (
          this.benefits[index].tiers[optionIndex].dependentCount! > 0 &&
          allDisabled
        ) {
          this.benefits[index].dependentValMsg = messages.dependentValMsg;
        }
      } else {
        if (
          this.benefits[index] &&
          Array.isArray(this.benefits[index].dependents!)
        ) {
          this.benefits[index].dependents!.map((dependent) => {
            dependent.disabled = true;
            return dependent;
          });
        }
      }
    },
    async bundledTierSelected(
      isSelected: boolean,
      tierPrice: number,
      selectedBenefit: iBenefit,
      index: number,
      optionIndex: number
    ) {
      // Set isSelected on the bundle's tiers
      this.benefits[index].tiers.forEach((option, i) => {
        option.isSelected = i === optionIndex ? isSelected : false;
      });
      if (isSelected) {
        this.enrollBenefit(index);
      }

      //clear any errors
      this.benefits[index].dependentValMsg = "";
      this.setFormDirty(true);

      this.benefits[index].tiers.forEach((option, i) => {
        option.isSelected = i === optionIndex ? isSelected : false;
      });

      //set up the dependents
      if (
        this.benefits[index] &&
        Array.isArray(this.benefits[index].dependents)
      ) {
        this.benefits[index].dependents!.map((dependent: iDependent) => {
          dependent.isSelected = false;
          return dependent;
        });
      }

      //loop through each benefit in the list to get our rates
      selectedBenefit.benefits!.forEach((benefit) => {
        if (isSelected) {
          const existingBenefit = this.calculateRates.find(
            (item) => item.benefitId === benefit.benefitId
          );
          if (existingBenefit) {
            existingBenefit.tierLevelName =
              this.benefits[index].tiers[optionIndex].tierLevelName!;
          } else {
            this.calculateRates.push({
              benefitId: benefit.benefitId,
              tierLevelName:
                this.benefits[index].tiers[optionIndex].tierLevelName!,
            });
            this.calculateRates = [...this.calculateRates];
          }

          this.benefits[index].tiers.map((option, index) => {
            if (index !== optionIndex) {
              option.isSelected = false;
            }
            return option;
          });

          this.disableDependents(index, optionIndex);
          var allDisabled = false;
          if (
            this.benefits[index] &&
            Array.isArray(this.benefits[index].dependents!)
          ) {
            allDisabled = this.benefits[index].dependents!.every(
              (dependent: iDependent) => {
                dependent.disabled;
              }
            );
          }
          if (
            this.benefits[index].tiers[optionIndex].dependentCount! > 0 &&
            allDisabled
          ) {
            this.benefits[index].dependentValMsg = messages.dependentValMsg;
          }
        } else {
          if (
            this.benefits[index] &&
            Array.isArray(this.benefits[index].dependents!)
          ) {
            this.benefits[index].dependents!.map((dependent) => {
              dependent.disabled = true;
              return dependent;
            });
          }
        }
      });
    },

    enrollBenefit(index: number) {
      this.benefits[index].isOpen = true;
      this.setFormDirty(true);
      const currentCategoryId = this.benefits[index].benefitCategoryId;

      // Waive other benefits in the same category
      this.benefits.forEach((b, i) => {
        if (
          i !== index &&
          b.benefitCategoryId &&
          b.benefitCategoryId === currentCategoryId
        ) {
          // Remove from calculateRates for the main benefit
          this.calculateRates = this.calculateRates.filter(
            (item) => item.benefitId !== b.benefitId
          );
          // If this is a bundle, also remove all child benefits from calculateRates
          if (Array.isArray(b.benefits)) {
            b.benefits.forEach((child) => {
              this.calculateRates = this.calculateRates.filter(
                (item) => item.benefitId !== child.benefitId
              );
            });
          }
          b.isWaived = true;
          // Also disable dependents and unselect tiers
          if (Array.isArray(b.dependents)) {
            b.dependents.forEach((d) => {
              d.disabled = true;
              d.isSelected = false;
            });
          }
          if (Array.isArray(b.tiers)) {
            b.tiers.forEach((t) => (t.isSelected = false));
          }
        }
        // If this is a bundle, check its benefits for matching category
        if (Array.isArray(b.benefits)) {
          b.benefits.forEach((child) => {
            if (
              child.benefitCategoryId &&
              child.benefitCategoryId === currentCategoryId
            ) {
              b.isWaived = true;
              if (Array.isArray(b.dependents)) {
                b.dependents.forEach((d) => {
                  d.disabled = true;
                  d.isSelected = false;
                });
              }
              if (Array.isArray(b.tiers)) {
                b.tiers.forEach((t) => (t.isSelected = false));
              }
            }
          });
        }
      });

      // If enrolling in a bundle, waive all other bundles with benefits inside of the same category
      if (this.benefits[index].benefitType === 1) {
        const selectedBundleCategoryIds = new Set(
          (this.benefits[index].benefits ?? []).map((b) => b.benefitCategoryId)
        );
        this.benefits.forEach((b, i) => {
          if (
            i !== index &&
            b.benefitType === 1 &&
            Array.isArray(b.benefits) &&
            b.benefits.some((child) =>
              selectedBundleCategoryIds.has(child.benefitCategoryId)
            )
          ) {
            b.isWaived = true;
            // Remove bundle's own rate
            this.calculateRates = this.calculateRates.filter(
              (item) => item.benefitId !== b.benefitId
            );
            // Remove all child benefit rates
            b.benefits.forEach((child) => {
              this.calculateRates = this.calculateRates.filter(
                (item) => item.benefitId !== child.benefitId
              );
            });
            if (Array.isArray(b.dependents)) {
              b.dependents.forEach((d) => {
                d.disabled = true;
                d.isSelected = false;
              });
            }
            if (Array.isArray(b.tiers)) {
              b.tiers.forEach((t) => (t.isSelected = false));
            }
          }
        });
      }

      // Mark this benefit as enrolled
      this.benefits[index].isWaived = false;

      // Add to calculateRates if needed (for single/unique)
      if (
        this.benefits[index].benefitRateTypeId === this.common.benefit.single ||
        this.benefits[index].benefitRateTypeId === this.common.benefit.unique
      ) {
        if (
          !this.calculateRates.some(
            (item) =>
              ((!this.benefits[index].benefits ||
                this.benefits[index].benefits!.length == 0) &&
                item.benefitId === this.benefits[index].benefitId) ||
              (this.benefits[index].benefits &&
                this.benefits[index].benefits!.length > 0 &&
                this.benefits[index].benefits!.some(
                  (benefit) => benefit.benefitId === item.benefitId
                ))
          )
        ) {
          var benefit = this.benefits[index] as iBenefit;

          if (benefit.benefits && benefit.benefits.length > 0) {
            benefit.benefits.forEach((benefit) => {
              this.calculateRates.push({
                benefitId: benefit.benefitId,
                tierLevelName: "",
              });
            });
          } else {
            this.calculateRates.push({
              benefitId: this.benefits[index].benefitId,
              tierLevelName: "",
            });
          }

          this.calculateRates = [...this.calculateRates];
        }
      }
    },

    async calculateBenefitRates() {
      if (!this.calculateRates || this.calculateRates.length === 0) {
        const benefitSummary: iBenefitSummary = {
          amountDueToday: 0,
          monthlyPremium: 0,
          amountDuePlanRates: [],
          monthlyPremiumPlanRates: [],
        };
        this.setBenefitRates(benefitSummary);
      }

      const params = {
        amountDueBenefits: this.calculateRates,
        customerParticipantId: this.getEnrollment.customerParticipantId,
      };

      const result = await apiClient
        .benefitsController()
        .postApiBenefitsCalculateRates(params);

      if (!result || result.error) {
        if (result.error) {
          this.validationMsg = result.error + messages.errorMsg;
        }
        return;
      } else if (result.data) {
        this.setBenefitRates(result.data);
      }
    },
    async toggleAccordion(index: number, action: string) {
      let isWaived: boolean | null = false;
      if (action === this.action.waive) {
        this.benefits[index].dependentValMsg = "";
        this.setFormDirty(true);
        isWaived = this.benefits[index].isWaived!;
        this.benefits[index].isOpen = false;
        this.benefits[index].isWaived = true;
        if (
          this.benefits[index] &&
          Array.isArray(this.benefits[index].dependents!)
        ) {
          this.benefits[index].dependents!.map((dependent) => {
            dependent.disabled = true;
            return dependent;
          });
        }
        this.benefits[index].tiers.map((option) => {
          option.isSelected = false;
          return option;
        });
        const indexToRemove = this.calculateRates.findIndex(
          (item: iCalculateRate) =>
            item.benefitId === this.benefits[index].benefitId
        );
        if (indexToRemove !== -1) {
          this.calculateRates.splice(indexToRemove, 1);
          this.calculateRates = [...this.calculateRates];
        }
        // If this is a bundle, also remove any matching child benefit from calculateRates
        if (
          this.benefits[index].benefitType === 1 &&
          Array.isArray(this.benefits[index].benefits)
        ) {
          this.benefits[index].benefits!.forEach((child) => {
            const childIndexToRemove = this.calculateRates.findIndex(
              (item: iCalculateRate) => item.benefitId === child.benefitId
            );
            if (childIndexToRemove !== -1) {
              this.calculateRates.splice(childIndexToRemove, 1);
              this.calculateRates = [...this.calculateRates];
            }
          });
        }
      }
      if (action === this.action.enroll) {
        this.enrollBenefit(index);
      }
      let benefitTotal = this.getBenefitAmount;
      let indexToUpdate = benefitTotal.findIndex(
        (benefit: { benefitId: number }) =>
          benefit.benefitId === this.benefits[index].benefitId
      );
      if (action === this.action.waive && isWaived === false) {
        if (
          this.benefits[index] &&
          Array.isArray(this.benefits[index].dependents!)
        ) {
          this.benefits[index].dependents!.map((dependent) => {
            dependent.isSelected = false;
            return dependent;
          });
        }
      }
    },
    async getBenefitSummary(): Promise<void> {
      const params = {
        customerParticipantId: this.getEnrollment.customerParticipantId,
      };
      const result = await apiClient.paymentController().getApiPayment(params);
      if (!result || result.error) {
        if (result.error) {
          this.validationMsg = result.error + messages.errorMsg;
        }
        return;
      } else if (result.data) {
        helper.setBenefitSummary(result.data.benefitSummary!);
      }
    },
    async getAllBenefits() {
      const params = {
        customerParticipantId: this.getEnrollment.customerParticipantId,
      };
      const result = await apiClient
        .benefitsController()
        .getApiBenefitsOpenEnrollmentAvailable(params);
      if (result.data) {
        this.bundles = result.data.bundles!;
        this.unbundledBenefits = result.data.unbundledBenefits!;

        this.benefits =
          benefitMapper.mapApiBundleToBenefits(
            this.bundles,
            this.dependents.map((d) => ({ ...d }))
          ) ?? [];
        this.benefits.map((benefit) => {
          benefit.isOpen = false;
          if (benefit.isWaived === false) {
            const coverageOption = benefit.tiers.find(
              (option) => option.isSelected === true
            );

            benefit.benefits?.forEach((ben) => {
              const benefitRate = {
                benefitId: ben.benefitId,
              };
              if (
                coverageOption &&
                ben.benefitRateTypeId === common.benefit.tier
              ) {
                Object.assign(benefitRate, {
                  tierLevelName: coverageOption?.tierLevelName,
                });
              }
              this.calculateRates.push(benefitRate);
              this.calculateRates = [...this.calculateRates];
            });
          }
        });

        const newBenefits =
          benefitMapper.mapApiBenefitsToBenefits(
            this.unbundledBenefits,
            this.dependents.map((d) => ({ ...d }))
          ) ?? [];
        this.benefits.push(...newBenefits);

        newBenefits.forEach((benefit) => {
          benefit.isOpen = false;
          if (benefit.isWaived === false) {
            const coverageOption = benefit.tiers.find(
              (option) => option.isSelected === true
            );
            const benefitRate = {
              benefitId: benefit.benefitId,
            };
            if (
              coverageOption &&
              benefit.benefitRateTypeId === common.benefit.tier
            ) {
              Object.assign(benefitRate, {
                tierLevelName: coverageOption?.tierLevelName,
              });
            }
            this.calculateRates.push(benefitRate);
            this.calculateRates = [...this.calculateRates];
          }
        });

        this.benefits.forEach((benefit) => {
          const electionDependentBenefits = Array.isArray(
            benefit.electionDependentBenefits
          )
            ? benefit.electionDependentBenefits
            : [];

          if (
            benefit.isWaived === false &&
            electionDependentBenefits.length > 0 &&
            Array.isArray(benefit.dependents)
          ) {
            electionDependentBenefits.forEach((depId) => {
              const dep = benefit.dependents!.find(
                (d) => d.electionDependentId === depId
              );
              if (dep) {
                dep.isSelected = true;
              }
            });
          } else if (
            benefit.isWaived === true &&
            Array.isArray(benefit.dependents)
          ) {
            // Clear selection for waived benefits
            benefit.dependents.forEach((dep) => (dep.isSelected = false));
          }
        });
        this.benefits.forEach((benefit, bIndex) => {
          // Only process if benefit is enrolled and has tiers
          if (benefit.isWaived === false && Array.isArray(benefit.tiers)) {
            const selectedOptionIndex = benefit.tiers.findIndex(
              (t) => t.isSelected
            );
            if (selectedOptionIndex !== -1) {
              this.disableDependents(bIndex, selectedOptionIndex);
            }
          }
        });
      } else {
        const benefitSummary: iBenefitSummary = {
          amountDueToday: 0,
          monthlyPremium: 0,
          amountDuePlanRates: [],
          monthlyPremiumPlanRates: [],
        };
        this.setBenefitRates(benefitSummary);
      }
    },
    async getAllDependents() {
      const params = {
        customerParticipantId: this.getEnrollment.customerParticipantId,
      };
      const result = await apiClient
        .dependentController()
        .getApiDependentGetAll(params);
      if (result.data) {
        this.dependents =
          benefitMapper.mapApiDependentsToDependents(result.data) ?? [];
        this.benefits = this.benefits.map((benefit) => {
          // For bundles, apply dependents logic to each child benefit as well
          if (benefit.benefitType === 1 && Array.isArray(benefit.benefits)) {
            benefit.benefits = benefit.benefits.map((child) => {
              child.dependents = this.dependents.map(
                (dependent: iDependent) => {
                  // ...existing logic...
                  return { ...dependent }; // <-- ensure new object
                }
              );
              return { ...child };
            });
          }

          // Always update top-level benefit.dependents
          benefit.dependents = this.dependents.map((dependent: iDependent) => {
            // ...existing logic...
            return { ...dependent }; // <-- ensure new object
          });

          return { ...benefit };
        });

        this.benefits.forEach((benefit, bIndex) => {
          //console.log("Processing benefit:", JSON.stringify(benefit, null, 2));
          // Only process if benefit is enrolled and has tiers
          if (benefit.isWaived === false && Array.isArray(benefit.tiers)) {
            const selectedOptionIndex = benefit.tiers.findIndex(
              (t) => t.isSelected
            );
            if (selectedOptionIndex !== -1) {
              this.disableDependents(bIndex, selectedOptionIndex);
            }
          }
        });
      }
    },
    async getProfileInfo() {
      const params = {
        customerParticipantId: this.getEnrollment.customerParticipantId,
      };
      const result = await apiClient.profileController().getApiProfile(params);
      if (result.data) {
        this.profile = result.data;
      }
    },
    disableDependents(bIndex: number, oIndex: number) {
      const benefit = this.benefits[bIndex];
      const coverageOption = benefit.tiers[oIndex];
      const depCount = coverageOption?.dependentCount ?? 0;
      const electionDependentBenefits = benefit.electionDependentBenefits ?? [];
      const dependentsArr = benefit.dependents ?? [];

      const selectedCount = benefit.dependents!.filter(
        (d: iDependent) => d.isSelected
      ).length;
      benefit.dependents!.forEach((dependent: iDependent) => {
        // 1. If no coverageOption or depCount < 1, disable
        if (!coverageOption || depCount < 1) {
          dependent.disabled = true;
          return;
        }

        // 2. If spouse is not included and this is a spouse, disable
        if (
          !coverageOption.includeSpouse &&
          dependent.relationshipName === common.dependentRelations.spouse
        ) {
          dependent.disabled = true;
          return;
        }

        // 3. If spouse is included, depCount is 1, and this is not a spouse, disable
        if (
          coverageOption.includeSpouse &&
          depCount === 1 &&
          dependent.relationshipName !== common.dependentRelations.spouse
        ) {
          dependent.disabled = true;
          return;
        }

        // 4. If this dependent is not in electionDependentBenefits and depCount matches electionDependentBenefits.length, disable
        if (
          electionDependentBenefits.indexOf(
            dependent.electionDependentId ?? -1
          ) === -1 &&
          depCount === electionDependentBenefits.length
        ) {
          dependent.disabled = true;
          return;
        }

        // 5. If spouse is included, depCount > 1, only one spot left (depCount - electionDependentBenefits.length === 1),
        // and this is not a spouse, and this dependent is not in electionDependentBenefits, and all selected are not spouse, disable
        if (
          coverageOption.includeSpouse &&
          depCount > 1 &&
          depCount - electionDependentBenefits.length === 1 &&
          dependent.relationshipName !== common.dependentRelations.spouse &&
          electionDependentBenefits.indexOf(
            dependent.electionDependentId ?? -1
          ) === -1 &&
          electionDependentBenefits.every((depId) =>
            dependentsArr.some(
              (d) =>
                depId === d.electionDependentId &&
                d.relationshipName !== common.dependentRelations.spouse
            )
          )
        ) {
          dependent.disabled = true;
          return;
        }

        // 6. If depCount is 1 and a dependent is already selected, disable all others
        if (depCount === 1 && !dependent.isSelected && selectedCount >= 1) {
          dependent.disabled = true;
          return;
        }

        // Otherwise, enabled
        dependent.disabled = false;
      });
    },
    changeDependent(isSelected: boolean, bIndex: number) {
      this.setFormDirty(true);
      const benefit = this.benefits[bIndex];
      const coverageOptionIndex = benefit.tiers.findIndex(
        (option) => option.isSelected
      );

      // Always recalculate allowed dependents after any change
      if (coverageOptionIndex !== -1) {
        this.disableDependents(bIndex, coverageOptionIndex);
      }
    },
    async submitHandler() {
      if (
        this.hasFileDeleteCheckHappened == false &&
        (await this.requiredFileOnWaivedBenefitCheck()) == true
      ) {
        this.isApprovalModalVisible = true;
        return;
      } else {
        this.hasFileDeleteCheckHappened = true;
      }

      let allWaived = true;
      let params: apiClient.AddBenefitDto[] = this.benefits.flatMap(
        (benefit: iBenefit): apiClient.AddBenefitDto[] => {
          if (benefit.isWaived) {
            return [
              {
                benefitIDs: benefit.benefitIds,
                isWaived: true,
              },
            ];
          }

          allWaived = false;
          let tierlevelName = "";
          let tierCodeID = 0;
          let dependentIds: number[] = [];

          if (benefit.benefitRateTypeId === common.benefit.tier) {
            const selectedCoverage = benefit.tiers.find(
              (option: iOpenEnrollmentBenefitTier) => option.isSelected
            );
            if (selectedCoverage) {
              tierlevelName = selectedCoverage.tierLevelName!;
              tierCodeID = selectedCoverage.tierCodeId!;
            }
          }

          if (Array.isArray(benefit.dependents)) {
            benefit.dependents.forEach((dependent) => {
              if (dependent.isSelected) {
                dependentIds.push(dependent.electionDependentId!);
              }
            });
          }

          return [
            {
              benefitIDs: benefit.benefitIds,
              tierCodeID,
              electionDependentBenefits: dependentIds,
              tierlevelName,
            },
          ];
        }
      );

      if (params.length === 0) {
        this.validationMsg =
          "No benefits available to save. " + messages.errorMsg;
        this.setFormDirty(false);
        return;
      }
      const addBenefitsDto = {
        addBenefitsDto: params,
        customerParticipantId: this.getEnrollment.customerParticipantId,
      };
      const result = await apiClient
        .benefitsController()
        .postApiBenefits(addBenefitsDto);
      if (!result || result.error) {
        if (result.error) {
          this.validationMsg = result.error + messages.errorMsg;
        }
        this.setFormDirty(false);
        return;
      } else if (result.data) {
        helper.setPageStep(this.common.pageStep.benefits);
        this.setFormDirty(false);
        let routeName = "documents";
        if (allWaived) {
          routeName = "confirmation";
          sessionStorage.setItem(
            common.storageVariables.pageStep,
            this.common.pageStep.confirmation.toString()
          );
          sessionStorage.setItem(
            common.storageVariables.allBenefitsWaived,
            "true"
          );
        } else {
          sessionStorage.removeItem(common.storageVariables.allBenefitsWaived);
        }
        this.$router.push({
          name: routeName,
        });
      }
    },
    async requiredFileOnWaivedBenefitCheck(): Promise<boolean> {
      var requiredFileBenefits = this.benefits.filter((benefit) => {
        return benefit.isWaived && benefit.hasUploadedRequiredFile;
      });

      if (requiredFileBenefits.length > 0) {
        this.approvalModalText =
          "<p>By waiving the following benefits, any required documents previously uploaded for the listed benefits will be removed.</p><ul>";

        requiredFileBenefits.forEach((benefit) => {
          var name = benefit.planName ? benefit.planName : benefit.bundleName;
          this.approvalModalText += "<li>" + name + "</li>";
        });

        this.approvalModalText += "</ul>";
      }

      return requiredFileBenefits.length > 0;
    },
    toggleAccordionOpen(index: number) {
      this.benefits[index].isOpen = !this.benefits[index].isOpen;
    },
    closeModal() {
      this.isApprovalModalVisible = false;
    },
    continueSubmitAfterRequiredFileCheck() {
      this.hasFileDeleteCheckHappened = true;
      this.submitHandler();
    },
    effectiveDate(date: string) {
      const effectiveDate = new Date(date);
      effectiveDate.setDate(effectiveDate.getDate());
      return helper.formatDate(effectiveDate.toString());
    },
    isDependentDisabled(benefit: iBenefit, dependent: iDependent): boolean {
      const coverageOption = benefit.tiers.find((t) => t.isSelected);
      const depCount = coverageOption?.dependentCount ?? 0;
      const electionDependentBenefits = benefit.electionDependentBenefits ?? [];
      const dependentsArr = benefit.dependents ?? [];

      // 1. If no coverageOption or depCount < 1, disable
      if (!coverageOption || depCount < 1) {
        return true;
      }

      // 2. If spouse is not included and this is a spouse, disable
      if (
        !coverageOption.includeSpouse &&
        dependent.relationshipName === common.dependentRelations.spouse
      ) {
        return true;
      }

      // 3. If spouse is included, depCount is 1, and this is not a spouse, disable
      if (
        coverageOption.includeSpouse &&
        depCount === 1 &&
        dependent.relationshipName !== common.dependentRelations.spouse
      ) {
        return true;
      }

      // 4. If this dependent is not in electionDependentBenefits and depCount matches electionDependentBenefits.length, disable
      if (
        electionDependentBenefits.indexOf(
          dependent.electionDependentId ?? -1
        ) === -1 &&
        depCount === electionDependentBenefits.length
      ) {
        return true;
      }

      // 5. If spouse is included, depCount > 1, only one spot left (depCount - electionDependentBenefits.length === 1),
      // and this is not a spouse, and this dependent is not in electionDependentBenefits, and all selected are not spouse, disable
      if (
        coverageOption.includeSpouse &&
        depCount > 1 &&
        depCount - electionDependentBenefits.length === 1 &&
        dependent.relationshipName !== common.dependentRelations.spouse &&
        electionDependentBenefits.indexOf(
          dependent.electionDependentId ?? -1
        ) === -1 &&
        electionDependentBenefits.every((depId) =>
          dependentsArr.some(
            (d) =>
              depId === d.electionDependentId &&
              d.relationshipName !== common.dependentRelations.spouse
          )
        )
      ) {
        return true;
      }

      // Otherwise, enabled
      return false;
    },
    areBenefitsValid() {
      if (!this.benefits.length) {
        return false;
      }

      // For each enrolled benefit, validate coverage options and dependents
      for (const benefit of this.benefits) {
        if (benefit.isWaived == undefined || benefit.isWaived === null) {
          return false; // Benefit must have isWaived defined
        }
        if (benefit.isWaived === false) {
          // If benefit has coverage options (tiers)
          if (benefit.benefitRateTypeId === common.benefit.tier) {
            const selectedOption = benefit.tiers.find((t) => t.isSelected);
            if (!selectedOption) {
              // Must select a coverage option
              return false;
            }
            // If the selected option requires dependents, check that enough are selected
            if (
              selectedOption.dependentCount &&
              selectedOption.dependentCount > 0
            ) {
              const selectedDependents = (benefit.dependents || []).filter(
                (d) => d.isSelected
              );
              if (selectedOption.dependentCount > 10) {
                // Allow any number from 1 up to dependentCount
                if (
                  selectedDependents.length === 0 ||
                  selectedDependents.length > selectedOption.dependentCount
                ) {
                  return false;
                }
              } else {
                // Require exactly dependentCount
                if (
                  selectedDependents.length !== selectedOption.dependentCount
                ) {
                  return false;
                }
              }
            }
          }
          // If benefit is a bundle, check the bundle's own coverage options
          if (benefit.benefitType === 1) {
            if (benefit.benefitRateTypeId === common.benefit.tier) {
              const selectedBundleOption = benefit.tiers.find(
                (t) => t.isSelected
              );
              if (!selectedBundleOption) {
                return false;
              }
              if (
                selectedBundleOption.dependentCount &&
                selectedBundleOption.dependentCount > 0
              ) {
                const selectedDependents = (benefit.dependents || []).filter(
                  (d) => d.isSelected
                );
                if (selectedBundleOption.dependentCount > 10) {
                  if (
                    selectedDependents.length === 0 ||
                    selectedDependents.length >
                      selectedBundleOption.dependentCount
                  ) {
                    return false;
                  }
                } else {
                  if (
                    selectedDependents.length !==
                    selectedBundleOption.dependentCount
                  ) {
                    return false;
                  }
                }
              }
            } else {
              // Also check each child benefit for coverage/dependent requirements
              if (Array.isArray(benefit.benefits)) {
                for (const child of benefit.benefits) {
                  if (child.benefitRateTypeId === common.benefit.tier) {
                    const selectedOption = child.tiers?.find(
                      (t) => t.isSelected
                    );
                    if (!selectedOption) {
                      return false;
                    }
                    if (
                      selectedOption.dependentCount &&
                      selectedOption.dependentCount > 0
                    ) {
                      const selectedDependents = (
                        child.dependents || []
                      ).filter((d) => d.isSelected);
                      if (selectedOption.dependentCount > 10) {
                        if (
                          selectedDependents.length === 0 ||
                          selectedDependents.length >
                            selectedOption.dependentCount
                        ) {
                          return false;
                        }
                      } else {
                        if (
                          selectedDependents.length !==
                          selectedOption.dependentCount
                        ) {
                          return false;
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }

      // All validations passed
      return true;
    },
    getBenefitsValidationMessage() {
      if (!this.benefits.length) {
        return "No benefits available.";
      }

      const messages: string[] = [];

      for (const benefit of this.benefits) {
        if (benefit.isWaived == undefined || benefit.isWaived === null) {
          messages.push(
            'Please select "Enroll" or "Waive" for all of your benefits in order to proceed'
          );
          continue;
        }
        if (benefit.isWaived === false) {
          if (benefit.benefitRateTypeId === common.benefit.tier) {
            const selectedOption = benefit.tiers.find((t) => t.isSelected);
            if (!selectedOption) {
              messages.push(
                "Please select a coverage option for " +
                  (benefit.planDescription ?? benefit.bundleName) +
                  "."
              );
            } else if (
              selectedOption.dependentCount &&
              selectedOption.dependentCount > 0
            ) {
              const selectedDependents = (benefit.dependents || []).filter(
                (d) => d.isSelected
              );
              if (selectedOption.dependentCount > 10) {
                if (
                  selectedDependents.length === 0 ||
                  selectedDependents.length > selectedOption.dependentCount
                ) {
                  messages.push(
                    "Please select dependent(s) for " +
                      (benefit.planDescription ?? benefit.bundleName)
                  );
                }
              } else {
                if (
                  selectedDependents.length !== selectedOption.dependentCount
                ) {
                  messages.push(
                    `Please select dependent(s) for ${
                      benefit.planDescription ?? benefit.bundleName
                    }.`
                  );
                }
              }
            }
          }
          // If benefit is a bundle, check the bundle's own coverage options
          if (benefit.benefitType === 1) {
            if (benefit.benefitRateTypeId === common.benefit.tier) {
              const selectedBundleOption = benefit.tiers.find(
                (t) => t.isSelected
              );
              if (!selectedBundleOption) {
                messages.push(
                  "Please select a coverage option for " +
                    (benefit.bundleName ?? "bundle") +
                    "."
                );
              } else if (
                selectedBundleOption.dependentCount &&
                selectedBundleOption.dependentCount > 0
              ) {
                const selectedDependents = (benefit.dependents || []).filter(
                  (d) => d.isSelected
                );
                if (selectedBundleOption.dependentCount > 10) {
                  if (
                    selectedDependents.length === 0 ||
                    selectedDependents.length >
                      selectedBundleOption.dependentCount
                  ) {
                    messages.push(
                      "Please select dependent(s) for " +
                        (benefit.bundleName ?? "bundle") +
                        "."
                    );
                  }
                } else {
                  if (
                    selectedDependents.length !==
                    selectedBundleOption.dependentCount
                  ) {
                    messages.push(
                      `Please select dependent(s) for ${
                        benefit.bundleName ?? "bundle"
                      }.`
                    );
                  }
                }
              }
            } else {
              // Also check each child benefit for coverage/dependent requirements
              if (Array.isArray(benefit.benefits)) {
                for (const child of benefit.benefits) {
                  if (child.benefitRateTypeId === common.benefit.tier) {
                    const selectedOption = child.tiers?.find(
                      (t) => t.isSelected
                    );
                    if (!selectedOption) {
                      messages.push(
                        "Please select a coverage option for " +
                          (child.planDescription ?? child.planName ?? "plan") +
                          "."
                      );
                    } else if (
                      selectedOption.dependentCount &&
                      selectedOption.dependentCount > 0
                    ) {
                      const selectedDependents = (
                        child.dependents || []
                      ).filter((d) => d.isSelected);
                      if (selectedOption.dependentCount > 10) {
                        if (
                          selectedDependents.length === 0 ||
                          selectedDependents.length >
                            selectedOption.dependentCount
                        ) {
                          messages.push(
                            "Please select dependent(s) for " +
                              (child.planDescription ??
                                child.planName ??
                                "plan") +
                              "."
                          );
                        }
                      } else {
                        if (
                          selectedDependents.length !==
                          selectedOption.dependentCount
                        ) {
                          messages.push(
                            `Please select dependent(s) for ${
                              child.planDescription ?? child.planName ?? "plan"
                            }.`
                          );
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
      const uniqueMessages = Array.from(new Set(messages));
      return uniqueMessages.join("<br/>\n");
    },
  },
});
